import { useEffect, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CircleNotch, CreditCard, ShieldCheck, Warning, Receipt } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

const cardStyle = {
  style: { base: { color: "#e5e5e5", fontSize: "15px", "::placeholder": { color: "#6b7280" } }, invalid: { color: "#f87171" } },
};

function fmtDate(iso) { if (!iso) return "—"; try { return new Date(iso).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }); } catch { return iso; } }

function CardForm({ clientSecret, setupIntentId, onDone }) {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const [agree, setAgree] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setBusy(true);
    try {
      const result = await stripe.confirmCardSetup(clientSecret, { payment_method: { card: elements.getElement(CardElement) } });
      if (result.error) { toast.error(result.error.message); setBusy(false); return; }
      await api.post("/billing/setup/complete", { setup_intent_id: setupIntentId });
      toast.success(t("commission.t_cardSaved"));
      onDone();
    } catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="commission-card-form">
      <div className="bg-white/5 border border-white/15 rounded-lg p-3"><CardElement options={cardStyle} /></div>
      <label className="flex items-start gap-2 text-xs text-neutral-400">
        <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-0.5" data-testid="commission-consent" />
        {t("commission.consent")}
      </label>
      <Button type="submit" disabled={!stripe || busy || !agree} className="rounded-full bg-gold text-obsidian hover:bg-gold/90 font-semibold" data-testid="commission-save-card-btn">
        {busy ? <CircleNotch className="animate-spin" /> : <><CreditCard className="mr-1" /> {t("commission.saveCard")}</>}
      </Button>
    </form>
  );
}

export default function CommissionBilling() {
  const { t } = useTranslation();
  const [status, setStatus] = useState(null);
  const [starting, setStarting] = useState(false);
  const [setup, setSetup] = useState(null); // {clientSecret, setupIntentId, stripePromise}

  const load = () => api.get("/billing/commission").then((r) => setStatus(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const startTrial = async () => {
    setStarting(true);
    try {
      const { data } = await api.post("/billing/trial/start");
      if (!data.publishable_key) { toast.error(t("commission.t_needPubKey")); setStarting(false); return; }
      setSetup({ clientSecret: data.client_secret, setupIntentId: data.setup_intent_id, stripePromise: loadStripe(data.publishable_key) });
    } catch (err) { toast.error(apiError(err)); }
    setStarting(false);
  };

  const setCadence = async (cadence) => {
    try { await api.put("/billing/cadence", { cadence }); setStatus((s) => ({ ...s, cadence })); toast.success(t("commission.t_cadenceUpdated")); }
    catch (err) { toast.error(apiError(err)); }
  };

  if (!status) return null;

  const badge = { trialing: ["bg-gold/20 text-gold", t("commission.trialing")], active: ["bg-emerald-500/20 text-emerald-300", t("commission.active")], past_due: ["bg-red-500/20 text-red-300", t("commission.past_due")], none: ["bg-white/10 text-neutral-400", t("commission.none")] }[status.status] || ["bg-white/10 text-neutral-400", status.status];

  return (
    <div className="border border-gold/25 rounded-xl p-6 bg-gradient-to-b from-gold/[0.06] to-transparent mb-8" data-testid="commission-billing">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-serif text-2xl flex items-center gap-2"><Receipt className="text-gold" /> {t("commission.title")}</h2>
          <p className="text-sm text-neutral-400 mt-1">{t("commission.subtitle", { pct: status.commission_rate_percent })}</p>
        </div>
        <span className={`text-xs px-3 py-1 rounded-full ${badge[0]}`} data-testid="commission-status-badge">{badge[1]}</span>
      </div>

      <div className="grid sm:grid-cols-3 gap-4 mt-5">
        <div className="bg-white/5 rounded-lg p-4"><div className="text-xs text-neutral-500">{t("commission.accrued")}</div><div className="text-2xl font-bold text-gold mt-1" data-testid="commission-pending">${status.pending_commission.toFixed(2)}</div><div className="text-xs text-neutral-500">{t("commission.orders", { n: status.pending_orders })}</div></div>
        <div className="bg-white/5 rounded-lg p-4"><div className="text-xs text-neutral-500">{t("commission.nextCharge")}</div><div className="text-lg font-semibold mt-1">{fmtDate(status.next_billing_at)}</div>{status.trial_ends_at && status.status === "trialing" && <div className="text-xs text-neutral-500">{t("commission.trialUntil", { date: fmtDate(status.trial_ends_at) })}</div>}</div>
        <div className="bg-white/5 rounded-lg p-4"><div className="text-xs text-neutral-500">{t("commission.rate")}</div><div className="text-2xl font-bold mt-1">{status.commission_rate_percent}%</div><div className="text-xs text-neutral-500">{t("commission.perSale")}</div></div>
      </div>

      {status.has_card && (
        <div className="mt-5">
          <div className="text-sm font-medium mb-2">{t("commission.billingFreq")}</div>
          <div className="flex gap-2">
            {["WEEKLY", "MONTHLY"].map((c) => (
              <button key={c} onClick={() => setCadence(c)} data-testid={`cadence-${c.toLowerCase()}`}
                className={`px-4 py-1.5 rounded-full text-sm border ${status.cadence === c ? "border-gold bg-gold/10 text-gold" : "border-white/15 text-neutral-400 hover:border-white/30"}`}>
                {c === "WEEKLY" ? t("commission.weekly") : t("commission.monthly")}
              </button>
            ))}
          </div>
        </div>
      )}

      {!status.has_card && !setup && (
        <div className="mt-6">
          {!status.publishable_key_set && (
            <div className="flex items-center gap-2 text-sm text-amber-300 bg-amber-500/10 border border-amber-500/25 rounded-lg p-3 mb-3">
              <Warning /> {t("commission.adminKeysWarn")}
            </div>
          )}
          <Button onClick={startTrial} disabled={starting} className="rounded-full bg-gold text-obsidian hover:bg-gold/90 font-semibold" data-testid="start-trial-btn">
            {starting ? <CircleNotch className="animate-spin" /> : <><ShieldCheck className="mr-1" /> {t("commission.startTrial")}</>}
          </Button>
        </div>
      )}

      {!status.has_card && setup && (
        <div className="mt-6 max-w-md">
          <Elements stripe={setup.stripePromise}>
            <CardForm clientSecret={setup.clientSecret} setupIntentId={setup.setupIntentId} onDone={() => { setSetup(null); load(); }} />
          </Elements>
        </div>
      )}

      {status.ledger?.length > 0 && (
        <div className="mt-6">
          <div className="text-sm font-medium mb-2">{t("commission.history")}</div>
          <div className="overflow-x-auto rounded-lg border border-white/10">
            <table className="w-full text-sm" data-testid="commission-ledger">
              <thead className="bg-white/5 text-neutral-400 text-xs"><tr><th className="text-left px-3 py-2">{t("commission.order")}</th><th className="text-right px-3 py-2">{t("commission.sale")}</th><th className="text-right px-3 py-2">{t("commission.commission")}</th><th className="text-left px-3 py-2">{t("commission.status")}</th><th className="text-left px-3 py-2">{t("commission.date")}</th></tr></thead>
              <tbody>
                {status.ledger.map((r) => (
                  <tr key={r.id} className="border-t border-white/5"><td className="px-3 py-2 font-mono text-xs">{r.wix_order_id}</td><td className="px-3 py-2 text-right">${r.gross.toFixed(2)}</td><td className="px-3 py-2 text-right text-gold">${r.commission.toFixed(2)}</td><td className="px-3 py-2 capitalize">{r.status}</td><td className="px-3 py-2 text-neutral-400">{fmtDate(r.created_at)}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
